// client/src/pages/pmPortal/PortalDocumentsPage.js
// Shared by both audiences. The server decides what each one may see; this page has no
// knowledge of scopes or flags and cannot widen the list by asking differently.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Table, Loading, Notice, Button, fmtDate, titleCase }
    from '../../components/secure/pm/PMKit';

const prettySize = (bytes) => {
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB'];
    let n = Number(bytes);
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const PortalDocumentsPage = () => {
    const api = usePmPortalApiClient();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/documents').then(setRows).catch(e => setError(e.message));
    }, [api]);

    const download = async (doc) => {
        setError('');
        try {
            const { data } = await api.get(
                `/api/pm-portal/documents/${doc.pm_document_id}/download`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.file_name || 'document';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        }
    };

    if (!rows && !error) return <Loading />;

    return (
        <>
            <PageHeader
                title="Documents"
                subtitle="Files the office has shared with you. Nothing appears here until it is deliberately released."
            />

            {error && <Notice tone="bad" title="Could not open the document" message={error} onDismiss={() => setError('')} />}

            <Panel title="Shared with you">
                <Table
                    columns={[
                        { key: 'title', label: 'Document' },
                        { key: 'doc_type', label: 'Type', render: r => (r.doc_type ? titleCase(r.doc_type) : '—') },
                        { key: 'scope', label: 'Relates to', render: r => titleCase(r.scope) },
                        { key: 'uploaded_at', label: 'Added', render: r => fmtDate(r.uploaded_at) },
                        { key: 'file_size', label: 'Size', align: 'right', render: r => prettySize(r.file_size) },
                        {
                            key: 'download', label: '', align: 'right',
                            render: r => <Button variant="ghost" onClick={() => download(r)}>Download</Button>,
                        },
                    ]}
                    rows={rows || []}
                    empty="Nothing has been shared with you yet."
                />
            </Panel>
        </>
    );
};

export default PortalDocumentsPage;
